import { Injectable, inject } from '@angular/core';
import { CoreHttpClient, MEASURES_ICONS } from '@bepatient/core';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { forkJoin, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { MyMeasuresCategory, MyMeasuresEntry } from './my-measures.models';

@Injectable()
export class MyMeasuresApiService {
  private http = inject(CoreHttpClient);
  private translate = inject(TranslateService);

  loadChart(url: string): Observable<any> {
    return this.http.get<any>(url, { withMobiCredentials: true });
  }

  loadCategories(): Observable<Array<MyMeasuresCategory>> {
    return this.http
      .get<any>('/api/measures/categories', {
        withMobiCredentials: true
      })
      .pipe(
        map<any, MyMeasuresCategory[]>(res => this.categoryListParser(res)),
        switchMap<MyMeasuresCategory[], Observable<any>>(categories =>
          forkJoin([
            of(categories),
            ...categories.map(category => this.loadMeasuresList(category.uuid))
          ])
        ),
        map<[MyMeasuresCategory[], MyMeasuresEntry[]], MyMeasuresCategory[]>(
          ([categories, ...measures]) =>
            categories.map((category, idx) => ({
              ...category,
              measures: measures[idx]
            }))
        )
      );
  }

  loadMeasuresList(categoryUuid?: string): Observable<Array<MyMeasuresEntry>> {
    return this.http
      .get(
        categoryUuid
          ? `/api/measures/categories/${categoryUuid}`
          : '/api/measures',
        {
          withMobiCredentials: true
        }
      )
      .pipe(map(res => this.measuresListParser(res)));
  }

  private categoryListParser(data: any): Array<MyMeasuresCategory> {
    return ((data && data.items) || []).map(
      item =>
        <MyMeasuresCategory>{
          uuid: item.uuid,
          name: item.title,
          color: item.color,
          abspath: item.abspath,
          measures: []
        }
    );
  }

  private measuresListParser(data: any): Array<MyMeasuresEntry> {
    return ((data && data.items) || []).map(
      item =>
        <MyMeasuresEntry>{
          title: item.meta && item.meta.title,
          lastEntryLabel: this.composeLastEntryLabel(
            item.meta && item.meta.last_activity
          ),
          color: item.meta && item.meta.color,
          href:
            item.links && item.links && item.links.self && item.links.self.href,
          numberOfEntriesLabel: this.composeNumberOfEntriesLabel(
            item.meta && item.meta.count
          ),
          subType: item.meta && item.meta.sub_type,
          icon: this.parseIcon(item),
          displayGraphToUser:
            (item.meta && item.meta.display_graph_to_user) ||
            (item.meta && item.meta.type === 'scoring')
        }
    );
  }

  private composeNumberOfEntriesLabel(numberOfEntries: number): string {
    const textToTranslate =
      numberOfEntries > 1
        ? 'MY_MEASURES.LIST.ENTRIES'
        : 'MY_MEASURES.LIST.ENTRY';
    return `${numberOfEntries} ${this.translate.instant(textToTranslate)}`;
  }

  private composeLastEntryLabel(lastEntryObject: { time: string }): string {
    if (!lastEntryObject) {
      return this.translate.instant('MY_MEASURES.LIST.NO_ENTRY');
    } else if (!!lastEntryObject.time) {
      return this.translate.instant('MY_MEASURES.LIST.LAST_ENTRY_TITLE', {
        time: moment(lastEntryObject.time).fromNow(true)
      });
    } else {
      return '';
    }
  }

  private parseIcon(measure: any): string {
    return measure && measure.meta && !measure.meta.icon
      ? MEASURES_ICONS[measure.meta.sub_type]
      : measure.meta.icon;
  }
}
